/**
 * Staff page — EntityTable framework (11E.4, migrated from the legacy
 * infinite-scroll Table). Filters: status, role/staff_type; search field.
 * Clickable rows -> detail (fixes "can't click staff card").
 *
 * Edit pre-load fix: "edit" row action fetches the full record by ID before
 * opening the modal (never trusts stale row-cache data) — same fix as
 * Students in 11E.3.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EntityTable } from '@/components/entity';
import { useTenantAwareEntityList } from '@/components/entity/useTenantAwareEntityList';
import { StaffFormModal } from '@features/people/components/StaffFormModal';
import {
  useDeactivateStaff,
  getStaff,
  type StaffResponse,
  type ListStaffPagedParams,
} from '@features/people/api/staff';
import { staffTableConfig } from '@features/people/config/staffEntityConfig';
import { ImportExportButtons } from '@features/imports';
import type { EntityActionKey } from '@/components/entity/types';

const PAGE_SIZE = 20;

export const Staff: React.FC = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(staffTableConfig.defaultSort ?? 'created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>(staffTableConfig.defaultOrder ?? 'desc');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>(
    staffTableConfig.defaultFilters ?? {},
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editStaff, setEditStaff] = useState<StaffResponse | undefined>();
  const [editLoading, setEditLoading] = useState(false);

  const queryParams: ListStaffPagedParams = {
    status: filters.status !== 'all' ? filters.status : undefined,
    role: filters.role !== 'all' ? filters.role : undefined,
    search: search || undefined,
    sort,
    order,
    page,
    page_size: PAGE_SIZE,
  };

  const { data: result, isLoading, error, refetch, isAllMode } = useTenantAwareEntityList<StaffResponse>({
    queryKey: ['staff', 'list', queryParams],
    apiPath: staffTableConfig.apiPath,
    params: queryParams as unknown as Record<string, string | number | boolean | undefined | null>,
  });
  const deactivateMutation = useDeactivateStaff();

  const handleSortChange = (newSort: string, newOrder: 'asc' | 'desc') => {
    setSort(newSort);
    setOrder(newOrder);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleRowClick = (staff: StaffResponse) => {
    navigate(`/staff/${staff.id}`);
  };

  /** Edit pre-load fix: always fetch fresh record by ID before opening modal. */
  const handleRowAction = async (action: EntityActionKey, staff: StaffResponse) => {
    if (action === 'view') {
      navigate(`/staff/${staff.id}`);
    } else if (action === 'edit') {
      setEditLoading(true);
      try {
        const fresh = await getStaff(staff.id);
        setEditStaff(fresh);
        setIsModalOpen(true);
      } catch {
        setEditStaff(staff); // fallback to row data if fetch fails
        setIsModalOpen(true);
      } finally {
        setEditLoading(false);
      }
    } else if (action === 'archive') {
      if (!window.confirm(`Deactivate ${staff.first_name} ${staff.last_name}?`)) return;
      await deactivateMutation.mutateAsync(staff.id);
    }
  };

  const total = result?.total ?? 0;
  const subtitle = isLoading ? 'Loading…' : `${total.toLocaleString()} team members`;

  return (
    <PageContainer
      title="Staff"
      subtitle={subtitle}
      data-testid="candeza-staff-page"
      data-flow="staff-page"
      actions={
        <>
          <ImportExportButtons
            entity="staff"
            entityLabel="Staff"
            selectedIds={selectedIds}
            exportStatus={filters.status !== 'all' ? filters.status : undefined}
            exportSearch={search || undefined}
          />
          <Button
            onClick={() => { setEditStaff(undefined); setIsModalOpen(true); }}
            data-testid="candeza-staff-add-btn"
            disabled={editLoading || isAllMode}
            title={isAllMode ? 'Select a specific school to add staff' : undefined}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Staff
          </Button>
        </>
      }
    >
      <Card padding="lg">
        <EntityTable
          config={staffTableConfig}
          result={result}
          isLoading={isLoading}
          error={error as Error | null}
          sort={sort}
          order={order}
          search={search}
          filters={filters}
          page={page}
          selectedIds={selectedIds}
          onSortChange={handleSortChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onPageChange={setPage}
          onSelectChange={setSelectedIds}
          onRowClick={handleRowClick}
          onRowAction={handleRowAction}
          onRetry={() => refetch()}
          isAllMode={isAllMode}
        />
      </Card>

      <StaffFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditStaff(undefined); }}
        staff={editStaff}
      />
    </PageContainer>
  );
};


